/* SelectedPlanBadge — plan-aware context strip shown above the register form
 * when the user arrives from a pricing CTA on the landing page.
 *
 * Pulls the chosen plan + cadence from URL (?plan=pro&cadence=annual) or
 * localStorage (`plan_intent`) so the user always sees what they're signing
 * up for. Provides an "edit" dropdown so they can switch plans without
 * navigating away, and a small escape hatch back to the free tier.
 */
import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Check, Sparkles } from 'lucide-react';

// Visible plans + canonical prices. Mirrors backend ACTIVE_PLAN_IDS + the
// homepage pricing lineup (Starter / Pro / Platinum / Done For You) so the
// signup picker, the homepage, and Stripe all agree. If billing.py changes a
// price or tier, update this list to match.
const PLANS = [
  { id: 'starter',  name: 'Starter',      price: 19.99,  blurb: 'For solo operators (single user)' },
  { id: 'pro',      name: 'Pro',          price: 49.99,  blurb: 'Full accounting + AI (5 users)', recommended: true },
  { id: 'platinum', name: 'Platinum',     price: 99.99,  blurb: 'Advisor call + priority support', popular: true },
  { id: 'done_for_you', name: 'Done For You', price: 299.99, blurb: 'A CPA/EA does the books for you' },
];

export default function SelectedPlanBadge({ planId, cadence, onChange }) {
  const [open, setOpen] = useState(false);
  const plan = useMemo(() => PLANS.find((p) => p.id === planId) || PLANS[0], [planId]);
  const isAnnual = cadence === 'annual';
  const monthly = plan.price;
  // Annual = true 15% off (owner directive, Oct 2026) — matches backend.
  const ANNUAL_MULT = 0.85;
  const annualMonthly = monthly > 0 ? +(monthly * ANNUAL_MULT).toFixed(2) : 0;
  const annualTotal = monthly > 0 ? +(monthly * 12 * ANNUAL_MULT).toFixed(2) : 0;

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (!e.target.closest('[data-plan-picker]')) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const isFree = plan.id === 'free';

  return (
    <div className="mb-6" data-testid="selected-plan-badge">
      <div
        className={`rounded-2xl border p-5 relative ${
          isFree
            ? 'border-[#E4E8E6] bg-[#F7F9F8] dark:border-white/10 dark:bg-white/[0.03]'
            : 'border-[#27B84A]/40 bg-[#F2FBF4] dark:border-emerald-500/30 dark:bg-[#0E1A11]'
        }`}
        data-plan-picker
      >
        <p className={`text-[11px] font-mono uppercase tracking-[0.22em] ${isFree ? 'text-[#596273] dark:text-zinc-500' : 'text-[#118A35] dark:text-emerald-300'}`}>
          {isFree ? 'You\'re starting on' : "You're signing up for"}
        </p>
        <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-baseline gap-2">
            <h3 className="text-2xl font-bold text-[#18202D] dark:text-white tracking-tight">{plan.name}</h3>
            <span className="text-[#9AA2AE] dark:text-zinc-500">·</span>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold text-[#18202D] dark:text-white" data-testid="selected-plan-price">
                {isFree ? '$0' : `$${(isAnnual ? annualMonthly : monthly).toFixed(2)}`}
              </span>
              <span className="text-[#596273] dark:text-zinc-400 text-sm">/mo</span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className={`text-xs font-mono uppercase tracking-wider px-2 py-1 rounded border flex items-center gap-1 transition-colors ${
              isFree
                ? 'text-[#596273] border-[#E4E8E6] hover:border-[#27B84A] hover:text-[#118A35] dark:text-zinc-300 dark:border-white/15 dark:hover:border-emerald-400 dark:hover:text-emerald-300'
                : 'text-[#118A35] hover:text-[#27B84A] border-[#27B84A]/40 hover:border-[#27B84A] dark:text-emerald-300 dark:hover:text-emerald-200 dark:border-emerald-500/30 dark:hover:border-emerald-400'
            }`}
            data-testid="selected-plan-edit"
          >
            {isFree ? 'Upgrade' : 'Edit'}
            <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        </div>
        {isAnnual && monthly > 0 && (
          <p className="mt-1 text-xs text-emerald-300/80 font-mono uppercase tracking-wider" data-testid="selected-plan-annual">
            Billed ${annualTotal.toFixed(2)}/year · You save ${(monthly * 12 - annualTotal).toFixed(2)}
          </p>
        )}
        <p className={`mt-3 text-xs flex items-center gap-1.5 ${isFree ? 'text-[#596273] dark:text-zinc-500' : 'text-[#596273] dark:text-zinc-400'}`}>
          <Sparkles className={`w-3 h-3 ${isFree ? 'text-[#9AA2AE] dark:text-zinc-500' : 'text-[#27B84A] dark:text-emerald-400'}`} />
          {isFree
            ? 'Start your 7-day free trial — cancel anytime.'
            : 'Start your 7-day free trial. You won\'t be charged until day 7. Cancel anytime.'}
        </p>

        {open && (
          <div className="absolute left-4 right-4 top-full mt-2 z-20 rounded-xl border border-[#E4E8E6] bg-white shadow-xl shadow-black/10 dark:border-white/10 dark:bg-zinc-950/95 dark:backdrop-blur-xl dark:shadow-2xl dark:shadow-black/50 overflow-hidden" data-testid="plan-picker-dropdown">
            <div className="p-2 flex items-center justify-between border-b border-[#E4E8E6] dark:border-white/5">
              <p className="text-[10px] font-mono uppercase tracking-wider text-[#596273] dark:text-zinc-500 px-2">Switch plan</p>
              {/* Inline monthly/annual mini-toggle */}
              <div className="flex p-0.5 rounded-full border border-[#E4E8E6] bg-[#F7F9F8] dark:border-white/10 dark:bg-white/5 text-[10px] font-mono uppercase tracking-wider">
                <button
                  type="button"
                  className={`px-2 py-0.5 rounded-full transition-colors ${!isAnnual ? 'bg-[#27B84A] text-white' : 'text-[#596273] hover:text-[#18202D] dark:text-zinc-400 dark:hover:text-white'}`}
                  onClick={() => { onChange?.(plan.id, 'monthly'); }}
                  data-testid="plan-picker-monthly"
                >
                  Monthly
                </button>
                <button
                  type="button"
                  className={`px-2 py-0.5 rounded-full transition-colors ${isAnnual ? 'bg-[#27B84A] text-white' : 'text-[#596273] hover:text-[#18202D] dark:text-zinc-400 dark:hover:text-white'}`}
                  onClick={() => { onChange?.(plan.id, 'annual'); }}
                  data-testid="plan-picker-annual"
                >
                  Annual −15%
                </button>
              </div>
            </div>
            {/* Social-proof nudge — only shown when starting from the free
                flow, where the user is exploring upgrades for the first time. */}
            {isFree && (
              <button
                type="button"
                onClick={() => { onChange?.('pro', cadence, { fromSocialProof: true }); setOpen(false); }}
                data-testid="plan-picker-social-proof"
                className="w-full text-left p-3 bg-gradient-to-r from-[#27B84A]/10 via-[#27B84A]/5 to-transparent border-b border-[#27B84A]/20 hover:from-[#27B84A]/15 dark:from-emerald-500/10 dark:via-emerald-500/5 dark:border-emerald-500/20 dark:hover:from-emerald-500/15 transition-colors flex items-start gap-2"
              >
                <Sparkles className="w-3.5 h-3.5 text-[#27B84A] dark:text-emerald-400 mt-0.5 shrink-0" />
                <span className="text-[11px] text-[#18202D] dark:text-emerald-100 leading-relaxed">
                  <strong className="text-[#118A35] dark:text-emerald-300">Most CountBucks users choose Pro</strong> — full double-entry accounting plus 200+ AI models for $49.99/mo. <span className="text-[#118A35] dark:text-emerald-400 underline decoration-[#27B84A]/50">Try Pro instead →</span>
                </span>
              </button>
            )}
            <ul className="max-h-72 overflow-y-auto">
              {PLANS.map((p) => {
                const pAnnual = p.price > 0 ? +(p.price * 0.85).toFixed(2) : 0;
                const pDisplay = isAnnual ? pAnnual : p.price;
                const isSelected = p.id === plan.id;
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => { onChange?.(p.id, cadence); setOpen(false); }}
                      data-testid={`plan-picker-${p.id}`}
                      className={`w-full flex items-center justify-between gap-3 p-3 text-left hover:bg-[#F7F9F8] dark:hover:bg-white/[0.04] transition-colors ${isSelected ? 'bg-[#27B84A]/5 dark:bg-emerald-500/5' : ''}`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-[#18202D] dark:text-white text-sm">{p.name}</span>
                          {p.popular && (
                            <span className="text-[9px] font-mono uppercase tracking-wider text-emerald-300 bg-emerald-500/10 px-1.5 py-0.5 rounded">
                              Popular
                            </span>
                          )}
                          {p.recommended && (
                            <span className="text-[9px] font-mono uppercase tracking-wider text-amber-600 bg-amber-500/10 dark:text-amber-300 px-1.5 py-0.5 rounded">
                              Recommended
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-[#596273] dark:text-zinc-500 truncate">{p.blurb}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold text-[#18202D] dark:text-white whitespace-nowrap">
                          {pDisplay === 0 ? 'Free' : `$${pDisplay.toFixed(2)}/mo`}
                        </p>
                        {isSelected && <Check className="w-3 h-3 text-[#27B84A] dark:text-emerald-400 ml-auto" />}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

